import { DataResult, State } from '@progress/kendo-data-query';
import {
  GridCustomCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../../../../common/global/helpers';
import Breadcrumbs from '../../../../../../../common/widgets/Breadcrumbs';
import Button from '../../../../../../../common/widgets/Button';
import { DataLoader } from '../../../../../../../common/widgets/DataLoader';
import Grid from '../../../../../../../common/widgets/Grid';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../../../config';
import { AttendenceTypeModel } from '../../../../../models/AttendenceType';
import { resources } from '../../../../../resources/resources';
import { timesheetsAdminService } from '../../../../../services/timesheets-admin';
import applicationCrumbs from '../../../../ApplicationCrumbs';
import {
  ColorCell,
  EditActionCell,
  HoursCell,
  ShortNotationCell,
  TypeDescriptionCell,
} from './CustomCells';
import AddNewAttendenceType from './Dialogs/AddNewAttendenceType';
import EditAttendenceType from './Dialogs/EditAttendenceType';

export interface AttendenceTypeListSate {
  data: AttendenceTypeModel[];
  dataState: State;
  total: number;
  currentSelectedType?: AttendenceTypeModel;
  showAddNewDialod: boolean;
  showEditDialog: boolean;
  reload: boolean;
}

const initialDataState: State = {
  skip: 0,
  take: config.grid.defaultPageSize,
};

export default function AttendenceTypeList() {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const [state, setState] = useState<AttendenceTypeListSate>({
    data: [] as AttendenceTypeModel[],
    dataState: initialDataState,
  } as AttendenceTypeListSate);

  const onDataReceived = (result: DataResult) => {
    setState(
      produce(d => {
        d.data = result.data;
        d.total = result.total;
        d.reload = false;
      }),
    );
  };

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setState(
      produce(d => {
        d.dataState = e.dataState;
      }),
    );
  };

  const handleOnSaveResponse = (isSaveSuccess: boolean) => {
    if (isSaveSuccess) {
      showSuccessToast(
        translate(resources.admin.timesheetsSettings.dialog.saveSuccessMessage),
      );
      setState(
        produce(d => {
          d.reload = true;
        }),
      );
    } else {
      showErrorToast(
        translate(resources.admin.timesheetsSettings.dialog.saveErrorMessage),
      );
    }
    setState(
      produce(d => {
        d.showAddNewDialod = false;
        d.showEditDialog = false;
      }),
    );
  };

  const handleOnDeleteResponse = (isDeleteSuccess: boolean) => {
    if (isDeleteSuccess) {
      showSuccessToast(
        translate(
          resources.admin.timesheetsSettings.dialog.deleteSuccessMessage,
        ),
      );
      setState(
        produce(d => {
          d.reload = true;
        }),
      );
    } else {
      showErrorToast(
        translate(resources.admin.timesheetsSettings.dialog.deleteErrorMessage),
      );
    }
    setState(
      produce(d => {
        d.showEditDialog = false;
      }),
    );
  };

  const handleEditAtendenceType = (item: AttendenceTypeModel) => {
    setState(
      produce(d => {
        d.currentSelectedType = item;
        d.showEditDialog = true;
      }),
    );
  };

  const EditTimesheetTypeCell = (props: GridCustomCellProps) => (
    <EditActionCell
      {...props}
      handleEditAtendenceType={() => handleEditAtendenceType(props.dataItem)}
    />
  );

  return (
    <div className="panel padding-xl timesheet-settings-admin">
      <Breadcrumbs
        entries={[crumbs.attendanceAdmin, crumbs.attendanceSettings]}
      />
      <h1>{translate(resources.admin.timesheetsSettings.types.title)}</h1>
      <div className="page-actions">
        <h4 className="timesheet-settings-subtitle">
          {translate(resources.admin.timesheetsSettings.types.defaultTimesheet)}
        </h4>
        <div className="page-action admin-actions">
          <Button
            className="btn btn-solid btn-sm"
            onClick={() => {
              setState(
                produce(d => {
                  d.showAddNewDialod = true;
                }),
              );
            }}
          >
            <span className="btn-text">
              {translate(resources.admin.timesheetsSettings.types.action)}
            </span>
          </Button>
        </div>
      </div>

      <DataLoader
        getData={timesheetsAdminService.getAttendenceTypes}
        dataState={state.dataState}
        onDataReceived={onDataReceived}
        bypassRequestDataIfNeeded={state.reload}
      />
      <div className="admin-grid timesheet-settings-grid">
        <Loader loading={!state.data} loader={<LoadingSpinner />}>
          <Grid
            data={state.data}
            pageable={{
              pageSizes: config.grid.pageSizes,
              pageSizeValue: state.dataState.take,
              info: false,
              previousNext: true,
            }}
            {...state.dataState}
            onDataStateChange={e => onDataStateChange(e)}
            total={state.total}
            sort={state.dataState.sort ?? []}
            columns={[
              {
                title: translate(resources.admin.timesheetsSettings.types.type),
                field: 'typeDescription',
                cell: TypeDescriptionCell,
              },
              {
                title: translate(
                  resources.admin.timesheetsSettings.types.duration,
                ),
                field: 'hours',
                cell: HoursCell,
              },
              {
                title: translate(
                  resources.admin.timesheetsSettings.dialog.shortNotation,
                ),
                field: 'shortNotation',
                cell: ShortNotationCell,
              },
              {
                title: translate(
                  resources.admin.timesheetsSettings.types.color,
                ),
                cell: ColorCell,
              },
              {
                cell: EditTimesheetTypeCell,
                headerClassName: 'grid-actions-cell',
              },
            ]}
          />
        </Loader>
      </div>
      {state.showAddNewDialod && (
        <AddNewAttendenceType
          onCloseAddDialog={() => {
            setState(
              produce(d => {
                d.showAddNewDialod = false;
              }),
            );
          }}
          onSaveResponse={handleOnSaveResponse}
        ></AddNewAttendenceType>
      )}
      {state.showEditDialog && (
        <EditAttendenceType
          onCloseAddDialog={() => {
            setState(
              produce(d => {
                d.showEditDialog = false;
              }),
            );
          }}
          onSaveResponse={handleOnSaveResponse}
          onDeleteResponse={handleOnDeleteResponse}
          attendanceType={
            state.currentSelectedType ?? ({} as AttendenceTypeModel)
          }
        ></EditAttendenceType>
      )}
    </div>
  );
}
